import { useHttpClient } from '@repo/api-client/react';
import {
  ChangePasswordRequestSchema,
  ChangePasswordResponseSchema,
  type ChangePasswordRequest,
  type ChangePasswordResponse,
} from '@repo/api-client/dtos';
import { useMutation } from '@tanstack/react-query';

import { postJSON } from '../internal/http';

/**
 * `POST /v1/auth/change-password` — rotate the authenticated user's
 * password.
 *
 * The server validates the current password via SuperTokens and
 * revokes all other open sessions on success. The current device's
 * bearer keeps working (see AuthService.changePassword for the full
 * trade-off note); the caller gets a `revokedOtherSessions` count so
 * the success copy can read "signed you out of N other devices".
 *
 * This hook deliberately doesn't touch the SessionProvider state —
 * it's purely the network round-trip + parsed envelope. The screen
 * that calls it is responsible for surfacing the success / error
 * states (notice sheet, inline form errors, etc.).
 */
export function useChangePassword() {
  const http = useHttpClient();

  const mutation = useMutation({
    mutationFn: (input: ChangePasswordRequest) => {
      // Validate the request body locally before flight so an
      // obviously-broken submit (empty new password, etc.) doesn't
      // burn a server round-trip + a slot in the per-user rate-
      // limit bucket.
      const body = ChangePasswordRequestSchema.parse(input);
      return postJSON(http, '/auth/change-password', body, ChangePasswordResponseSchema);
    },
  });

  return {
    changePassword: mutation.mutateAsync as (
      input: ChangePasswordRequest,
    ) => Promise<ChangePasswordResponse>,
    loading: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  };
}
